'use client'

import * as React from 'react'

import { Input } from '../components'

interface FormattedPriceInputProps extends Omit<
  React.ComponentProps<'input'>,
  'onChange' | 'value' | 'type' | 'inputMode' | 'size'
> {
  value: number
  onChange: (value: number) => void
}

const FormattedPriceInput = React.forwardRef<HTMLInputElement, FormattedPriceInputProps>(
  ({ value, onChange, className, ...props }, ref) => {
    const [displayValue, setDisplayValue] = React.useState('')

    React.useEffect(() => {
      const currentNumeric = parseFloat(displayValue.replace(/\./g, '').replace(',', '.'))

      // If external value (prop) is effectively same as display, don't update
      // This preserves "12," when value is 12
      if ((value === 0 && displayValue === '') || (!isNaN(currentNumeric) && currentNumeric === value)) {
        return
      }

      if (value === 0) {
        setDisplayValue('')
      } else {
        const parts = value.toString().split('.')
        const int = parts[0] ?? '0'
        const dec = parts[1]
        const formattedInt = int.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
        setDisplayValue(dec ? `${formattedInt},${dec}` : formattedInt)
      }
    }, [value, displayValue])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value

      // Allow digits, dots, and comma only
      if (!/^[\d,.]*$/.test(raw)) return

      // Normalize: remove dots
      const clean = raw.replace(/\./g, '')

      // Split by comma
      const parts = clean.split(',')
      if (parts.length > 2) return // Only one comma allowed

      const intPart = parts[0] ?? ''
      const decPart = parts[1]

      if (intPart.length > 16) return // Prevent excessive length

      // Format integer part
      const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.')

      let newDisplay = formattedInt
      let cleanForParse = intPart

      if (decPart !== undefined) {
        // Limit to 2 decimal places
        const limitedDec = decPart.slice(0, 2)
        newDisplay = `${formattedInt},${limitedDec}`
        cleanForParse = `${intPart}.${limitedDec}`
      }

      setDisplayValue(newDisplay)

      // Calculate numeric value
      const num = parseFloat(cleanForParse)
      onChange(isNaN(num) ? 0 : num)
    }

    return (
      <Input
        ref={ref}
        type='text'
        inputMode='decimal'
        data-slot='formatted-price-input'
        className={className}
        value={displayValue}
        onChange={handleChange}
        {...props}
      />
    )
  },
)

FormattedPriceInput.displayName = 'FormattedPriceInput'

export { FormattedPriceInput }
